import type { AppState } from './types.js'

const STATE_KEY = 'kil:v1:state'

export const DEFAULT_STATE: AppState = {
  indoor: { t: 21, rh: 55 },
  outdoor: { t: 8, rh: 80 },
  surfaceOffsetK: 3,
}

function isReading(value: unknown): value is { t: number; rh: number } {
  if (typeof value !== 'object' || value === null) return false
  const r = value as Record<string, unknown>
  return (
    typeof r['t'] === 'number' &&
    Number.isFinite(r['t']) &&
    typeof r['rh'] === 'number' &&
    Number.isFinite(r['rh'])
  )
}

/** Liest den letzten Zustand. Bei fehlendem, kaputtem oder fremdem Inhalt: Standardwerte. */
export function loadState(): AppState {
  try {
    const raw = localStorage.getItem(STATE_KEY)
    if (!raw) return DEFAULT_STATE
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_STATE
    const candidate = parsed as Record<string, unknown>
    if (!isReading(candidate['indoor']) || !isReading(candidate['outdoor'])) return DEFAULT_STATE
    const offset = candidate['surfaceOffsetK']
    return {
      indoor: candidate['indoor'],
      outdoor: candidate['outdoor'],
      surfaceOffsetK:
        typeof offset === 'number' && Number.isFinite(offset)
          ? offset
          : DEFAULT_STATE.surfaceOffsetK,
    }
  } catch {
    return DEFAULT_STATE
  }
}

export function saveState(state: AppState): void {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(state))
  } catch {
    // Privater Modus oder volles Quota: Persistenz ist optional, die App laeuft weiter.
  }
}

/** Ruft `fn` erst auf, wenn `delay` ms lang kein weiterer Aufruf kam. */
export function debounce<T extends unknown[]>(
  fn: (...args: T) => void,
  delay: number,
): (...args: T) => void {
  let handle: ReturnType<typeof setTimeout> | undefined
  return (...args: T) => {
    if (handle !== undefined) clearTimeout(handle)
    handle = setTimeout(() => fn(...args), delay)
  }
}
